import {
    Table,
    TableBody,
    TableCell,
    TableCellStack,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/Table/Table';
import { type AppSession } from '@/features/reports/api';
import { APP_SESSIONS_COPY as COPY } from '@/features/reports/copy';
import { formatWhen } from '@/lib/format';

/**
 * The app version log's rows (ALS-39), as App usage's session log shows them folded and in full
 * (ALS-160): every column a support lookup matches against a ticket.
 */
export function SessionsTable({ rows }: { rows: AppSession[] }) {
    return (
        // Seven columns do not fit a phone, so below 768 each row becomes a card: the person
        // leads, and every column the lookup is for (device, version, action, when) gets a
        // labelled line of its own rather than sitting off-screen behind a sideways swipe.
        <Table stack className="table-stack--wide-labels">
            <TableHeader>
                <TableRow>
                    <TableHead>{COPY.columns.name}</TableHead>
                    <TableHead>{COPY.columns.email}</TableHead>
                    <TableHead>{COPY.columns.platform}</TableHead>
                    <TableHead>{COPY.columns.appVersion}</TableHead>
                    <TableHead>{COPY.columns.action}</TableHead>
                    <TableHead>{COPY.columns.ip}</TableHead>
                    <TableHead>{COPY.columns.when}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.id}>
                        <TableCell data-stack="title" className="font-medium md:whitespace-nowrap">
                            <TableCellStack>
                                <span>{row.name}</span>
                                {/* The email column's value, under the name where that column is gone. */}
                                {row.email ? (
                                    <span className="font-normal break-all md:hidden">
                                        {row.email}
                                    </span>
                                ) : null}
                            </TableCellStack>
                        </TableCell>
                        <TableCell data-stack="hide" className="text-muted-foreground">
                            {row.email ?? ''}
                        </TableCell>
                        <TableCell
                            data-stack="meta"
                            data-label={COPY.columns.platform}
                            className="md:whitespace-nowrap"
                        >
                            {[row.platform, row.osVersion].filter(Boolean).join(' ')}
                        </TableCell>
                        <TableCell
                            data-stack="meta"
                            data-label={COPY.columns.appVersion}
                            className="tabular-nums"
                        >
                            {row.appVersion ?? ''}
                        </TableCell>
                        <TableCell data-stack="meta" data-label={COPY.columns.action}>
                            {row.action}
                        </TableCell>
                        {/* Monospaced: an IP is read digit by digit when it is being matched
                            against a support ticket. */}
                        <TableCell
                            data-stack="meta"
                            data-label={COPY.columns.ip}
                            className="font-mono text-xs md:whitespace-nowrap"
                        >
                            {row.ipAddress ?? ''}
                        </TableCell>
                        <TableCell
                            data-stack="meta"
                            data-label={COPY.columns.when}
                            className="text-muted-foreground md:whitespace-nowrap"
                        >
                            {row.signedInAt ? formatWhen(row.signedInAt) : COPY.noTime}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
